// Runtime resource limits — the "sane engine limits" that stop a script from
// wedging or OOMing the host, however it got there (docs/trust-model.md). The
// capability sandbox ("the box is the language") already prevents a script from
// reaching outside the game; these bound what it can do to the machine WITHIN
// the box: burn CPU forever, or allocate without end.
//
// They are enforced identically on every tier (server relay, browser player,
// fidelity-rig dummy client), so a script that hits a limit traps at the SAME
// point everywhere. Because fuel and heap are invisible to script OUTPUT, a
// well-behaved script's byte-for-byte output is unchanged — the fidelity rig
// keeps comparing exactly what it did before.
// Per-entry CPU budget. Units are loop back-edges + function entries executed
// within one host->script call (each exported entry — event, dispatcher,
// register-channels, init — resets the budget to this). Sized so a genuinely
// unbounded loop or runaway recursion traps in ~1-2s of wall time, while any
// realistic per-event workload — even a heavy one-off like decompressing a
// level raster — completes far under it. Fits in a signed i32 (< 2^31).
export const FUEL_BUDGET = 500_000_000;
// Linear-memory ceiling. Every script allocation funnels through the host bump
// allocator (there is no inline wasm allocator), which refuses to grow past
// this; the wasm memory's maxPages is the hard backstop for any path that
// bypasses bump. 64 MiB is generous for a 2D-era mod (framebuffers, level
// rasters, PNG atlases, compression scratch) yet bounds a runaway allocation or
// a decompression bomb well below the shared server VM's memory.
export const MAX_HEAP_PAGES = 1024; // 64 MiB / 64 KiB per page
export const MAX_HEAP_BYTES = MAX_HEAP_PAGES * 65536;
// The escaped generation (docs/memory-model.md §"Stage 2 mechanism") holds the
// persistent state a module that reassigns a global to a heap value keeps across
// events — it must survive the per-event young rewind. It is carved off the low
// end of the heap for a module whose analysis reports `usesEscapedHeap`; every
// other module keeps the whole heap as one young region, byte-identical to before.
//
// The escaped/young split is DYNAMIC (docs/memory-model.md §"Follow-ons"). Rather
// than a fixed reserve, `init` allocates every global's backing at the low end so
// the young region is genuinely empty between events; the host then plants the
// young base a headroom above the globals and RAISES it (growing the escaped
// region) at event boundaries under memory pressure. A small script commits only
// its globals plus the headroom below; a large one grows toward the ceiling
// instead of hitting a fixed wall.
// Headroom reserved for promoted escaped objects immediately above the globals,
// at the first event. The young base starts here; growth adds more. 1 MiB comfortably
// holds the incremental-accumulation workloads that escape (a roster grows an
// entry per event, well inside this) before the first growth boundary. Multiple of 4.
export const ESCAPED_HEAP_INITIAL_HEADROOM = 1 * 1024 * 1024;
// Ceiling the escaped generation may grow to (bytes from its base). Bounds how
// far the split can climb, leaving MAX_HEAP_BYTES − this for the young generation:
// 48 MiB of persistent state against a 64 MiB heap keeps ≥16 MiB for scratch.
// Multiple of 4.
export const ESCAPED_HEAP_MAX_BYTES = 48 * 1024 * 1024;
// One growth increment for the escaped ceiling — a steadily-growing structure
// crosses only a handful of grow events before settling rather than reallocating
// page-by-page. Multiple of 4.
export const ESCAPED_HEAP_GROW_STEP = 4 * 1024 * 1024;
// Grow the escaped ceiling when the region's high-water mark comes within this
// much of the current limit — headroom so a single event's promotions don't
// overrun the region before the next boundary can raise it. Multiple of 4.
export const ESCAPED_HEAP_GROW_HEADROOM = 512 * 1024;
// Image limits. Script-created images (image_literal / image_from_bytes /
// image_from_png_bytes / image_from_bitmap_bytes) hold their palette-index
// pixels in HOST memory (one byte per pixel), OUTSIDE the wasm linear memory —
// so MAX_HEAP_BYTES does not bound them. These do. Without them a script could
// name a 50000x50000 image_literal (a ~2.5 GB host allocation from three i32
// args) or mint a fresh image every frame in an unbounded loop and exhaust host
// memory while staying well under its fuel and heap caps. A single 2D-era asset
// is tiny — a whole NES screen is 256x240 = 61,440 px — so these ceilings are
// generous: they only bite pathological sizes. Enforced in the shared
// ImageStore / decode path (emulator-kit.ts), so every tier traps at the same
// point via MunosResourceError, exactly like the heap cap.
export const MAX_IMAGE_PIXELS = 1 << 21; // 2,097,152 px (~2 MiB) per image
export const MAX_IMAGE_STORE_BYTES = 32 * 1024 * 1024; // 32 MiB of images per instance
// Thrown by the bump allocator when a script would exceed MAX_HEAP_BYTES. A
// distinct type so a host can tell "script asked for too much memory" apart from
// an internal bug; it propagates through the wasm call like a trap and aborts
// the current event, exactly as running out of fuel does.
export class MunosResourceError extends Error {
    constructor(message) {
        super(message);
        this.name = "MunosResourceError";
    }
}
//# sourceMappingURL=limits.js.map